import React from 'react'
import styled from 'styled-components'
import Box from '@material-ui/core/Box'
import {Label} from 'lib/ui/typography'
import Icon from 'lib/ui/Icon'
import IconButton from 'lib/ui/IconButton'
import {routesWithValue} from 'lib/url'
import {Form, useForms} from 'organization/Event/FormsProvider'
import {useEventRoutes} from 'organization/Event/EventRoutes'
import {useOrganization} from 'organization/OrganizationProvider'
import {api} from 'lib/url'
import {useToggle} from 'lib/toggle'
import Menu from 'lib/ui/Menu'
import MenuItem from 'lib/ui/Menu/MenuItem'
import DeleteIcon from 'assets/images/ui/icons/trash.svg'
import DuplicateIcon from 'assets/images/ui/icons/duplicate.svg'
import DownloadIcon from 'assets/images/ui/icons/download.svg'
import QuestionIcon from 'assets/images/ui/icons/question.svg'
import StatisticsIcon from 'assets/images/ui/icons/statistics.svg'
import Divider from '@material-ui/core/Divider'
import {RelativeLink} from 'lib/ui/link/RelativeLink'
import ConfirmDialog from 'lib/ui/ConfirmDialog'
import {Link} from 'react-router-dom'
import {useLanguages, useRemoveTranslationKeys} from 'Event/LanguageProvider'
import {useSaveTemplate} from 'Event/TemplateUpdateProvider'
import {useTranslate} from 'Event/LanguageProvider/translations'
import {Language} from 'Event/LanguageProvider/language'
import {useGetQuestionTranslations} from 'organization/Event/Form'

export type FormItemProps = {
  form: Form
}

export default function FormItem(props: FormItemProps) {
  const {form} = props
  const updateTemplate = useSaveTemplate()
  const {add, remove} = useForms()
  const eventRoutes = useEventRoutes()
  const duplicate = useDuplicate()
  const save = useSave()
  const deleteForm = useDelete()
  const exportForm = useExport()
  const {flag: duplicateProcessing, toggle: toggleProcessing} = useToggle()
  const {flag: deleteProcessing, toggle: toggleDeleteProcessing} = useToggle()
  const {flag: exportProcessing, toggle: toggleExportProcessing} = useToggle()
  const translate = useTranslate()
  const formRoute = (form: Form) =>
    routesWithValue(':form', String(form.id), eventRoutes.forms[':form'])
  const languages = useLanguages()
  const removeTranslationKeys = useRemoveTranslationKeys()

  const {
    questions: translatedQuestions,
    translations: questionsTranslations,
  } = useGetQuestionTranslations({form, questions: form.questions})

  const addDuplicated = (form: Form) => {
    type TranslationMap = {[key: string]: {[messageKey: string]: string}}

    const forTranslate = {
      ...questionsTranslations,
      [`form_${form.id}_submitted_message`]: form.submitted_message,
      [`form_${form.id}_resubmit_button_label`]: form.resubmit_button_label,
      [`form_${form.id}_submit_label`]: form.submit_label,
      [`form_${form.id}_field_required_message`]: form.field_required_message,
    }

    const translations = languages.reduce(
      (acc: TranslationMap, language: Language) => {
        acc[language.name] = Object.fromEntries(
          Object.entries(forTranslate).map(([key, text]) => {
            return [key, translate({text, target: language.name})]
          }),
        )

        return acc
      },
      {},
    )

    // update template with all translations at once, because each update is 1 request to the API
    // also that prevent from bug where it don't show translations
    updateTemplate({
      localization: {translations},
    })

    const translatedForm = {
      ...form,
      questions: translatedQuestions,
      submitted_message: `{{form_${form.id}_submitted_message}}`,
      resubmit_button_label: `{{form_${form.id}_resubmit_button_label}}`,
      submit_label: `{{form_${form.id}_submit_label}}`,
      field_required_message: `{{form_${form.id}_field_required_message}}`,
    }

    save(translatedForm).then(() => {
      add(translatedForm)
    })
  }

  const handleDuplicate = (form: Form) => {
    if (duplicateProcessing) {
      return
    }
    toggleProcessing()
    duplicate(form)
      .then(addDuplicated)
      .finally(() => {
        toggleProcessing()
      })
  }

  const handleDelete = (form: Form) => {
    if (deleteProcessing) {
      return
    }

    toggleDeleteProcessing()
    deleteForm(form)
      .then(() => {
        const localizationUpdate = removeTranslationKeys(`form_${form.id}`)
        if (localizationUpdate) {
          updateTemplate({
            localization: localizationUpdate,
          })
        }
        remove(form)
      })
      .finally(() => {
        toggleDeleteProcessing()
      })
  }

  const handleExport = (form: Form) => {
    if (exportProcessing) {
      return
    }
    toggleExportProcessing()
    exportForm(form).then(() => {
      toggleExportProcessing()
    })
  }

  return (
    <Container aria-label="form">
      <RelativeLink disableStyles to={formRoute(form).root}>
        <Label aria-label="form-name">{form.name}</Label>
      </RelativeLink>

      <Menu
        button={({open}) => (
          <IconButton onClick={open} aria-label="view form actions">
            <Icon className="fas fa-ellipsis-h" color="black" iconSize={18} />
          </IconButton>
        )}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <div>
          <ConfirmDialog
            onConfirm={() => handleDelete(form)}
            aria-label="delete form confirm description"
            description="Are you sure you wish to delete this form?"
          >
            {(confirm) => (
              <MenuItem
                icon={DeleteIcon}
                children="Delete"
                disabled={deleteProcessing}
                onClick={confirm}
              />
            )}
          </ConfirmDialog>
        </div>
        <Divider />
        <MenuItem
          icon={DuplicateIcon}
          children="Duplicate"
          disabled={duplicateProcessing}
          onClick={() => handleDuplicate(form)}
        />
        <Divider />
        <MenuItem
          icon={DownloadIcon}
          children="Download Submissions"
          disabled={exportProcessing}
          onClick={() => handleExport(form)}
        />
        <Divider />
        <Link to={formRoute(form).moderation.root}>
          <MenuItem icon={QuestionIcon} disabled={exportProcessing}>
            Moderation
          </MenuItem>
        </Link>
        <Divider />
        <RelativeLink disableStyles to={formRoute(form).statistics}>
          <MenuItem icon={StatisticsIcon} children="View Statistics" />
        </RelativeLink>
      </Menu>
    </Container>
  )
}

function useDuplicate() {
  const {client} = useOrganization()
  return (form: Form) => {
    const url = api(`/forms/${form.id}/duplicate`)
    return client.post<Form>(url)
  }
}

function useSave() {
  const {client} = useOrganization()
  return (form: Partial<Form>) => {
    const url = api(`/forms/${form.id}`)
    return client.patch<Form>(url, form)
  }
}

function useDelete() {
  const {client} = useOrganization()
  return (form: Form) => {
    const url = api(`/forms/${form.id}`)
    return client.delete<Form>(url)
  }
}

function useExport() {
  const {client} = useOrganization()
  return (form: Form) => {
    const url = api(`/forms/${form.id}/submissions/export`)
    return client.get(url)
  }
}

export const formTranslationComponentId = (form: Form) => {
  return `form_${form.id}`
}

export const Container = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${(props) =>
    `${props.theme.spacing[4]} ${props.theme.spacing[3]}`} !important;
  border-bottom: 1px solid ${(props) => props.theme.colors.gray300} !important;
`
